import ClashWorker from './assemblyClash.worker?worker';
import type { ClashAnswer, ClashAsk, SentPart } from './assemblyClash.worker';
import type { ClashMarks, ClashPart, ClashProfile, StandingOverlap } from './assemblyClash';
import type { Assembly } from './assemblyPose';

// WHAT A QUESTION STILL WAITING IS TOLD WHEN ITS SCENE GOES: the scene was rebuilt (a part added to
// the picture), not a measure that failed. Asked again of the new one (see sceneClosed).
const SCENE_CLOSED = 'the scene was closed';
export const sceneClosed = (e: unknown) => e instanceof Error && e.message === SCENE_CLOSED;

// THE MEASURING, DONE SOMEWHERE ELSE. A movement's profile is sixty-one moments of two parts'
// surfaces against each other, and on a piece a dozen parts make it is seconds of work. It was
// done on the page's own thread, in slices handed out between frames — and a slice was a third
// of a second: opening a heavy sub-step drew five frames a second until the numbers were in,
// playing or not (measured: blocks of 280 to 440 ms, one after another).
//
// So a worker has a copy of the parts and does it there. The page keeps its own — the scene
// paints the marks onto them by face number, and the two copies number their faces alike,
// being the same points in the same order.
//
// ONE PER SCENE, made at the first question and ended with the scene: the parts are handed over
// once, and the trees built over them are kept there for every question after.

type Geometry = ClashPart['meshes'][number];

// A mesh's points as three plain numbers apiece. Most are that already; one whose file packs
// them otherwise (interleaved with its normals, or as scaled integers) is read out point by point.
function pointsOf(g: Geometry): Float32Array {
  const pos = g.attributes.position;
  const plain = pos as import('three').BufferAttribute;
  if (!('isInterleavedBufferAttribute' in pos) && plain.array instanceof Float32Array
    && plain.itemSize === 3 && !plain.normalized) return plain.array;
  const out = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i += 1) {
    out[i * 3] = pos.getX(i);
    out[i * 3 + 1] = pos.getY(i);
    out[i * 3 + 2] = pos.getZ(i);
  }
  return out;
}

const cornersOf = (g: Geometry): SentPart['meshes'][number]['index'] => {
  const held = g.index?.array;
  if (!held) return null;
  return held instanceof Uint32Array || held instanceof Uint16Array ? held : Uint32Array.from(held);
};

export type ClashRemote = {
  profile: (asm: Assembly, step: number, moments?: readonly number[]) => Promise<ClashProfile>;
  marks: (asm: Assembly, step: number, t: number) => Promise<ClashMarks | null>;
  hidden: (asm: Assembly, step: number, eyes: readonly (readonly number[])[], cams?: readonly (readonly number[])[]) => Promise<number[]>;
  overlaps: (asm: Assembly, pairs: readonly (readonly [string, string])[]) => Promise<StandingOverlap[]>;
  end: () => void;
};

export function clashRemote(parts: ClashPart[]): ClashRemote {
  const worker = new ClashWorker();
  const waiting = new Map<number, { done: (v: unknown) => void; fail: (e: Error) => void }>();
  let n = 0;
  // A question is one message and its answer another, matched by number.
  // AND ONCE ENDED, EVERY QUESTION IS REFUSED AT ONCE: posted to a worker that has been terminated,
  // it was never answered, and the candidate views asked of a scene just replaced spun for ever.
  let ended = false;
  const ask = <T>(m: ClashAsk extends infer A ? (A extends { n: number } ? Omit<A, 'n'> : never) : never) =>
    new Promise<T>((done, fail) => {
      if (ended) { fail(new Error(SCENE_CLOSED)); return; }
      n += 1;
      waiting.set(n, { done: done as (v: unknown) => void, fail });
      worker.postMessage({ ...m, n });
    });
  const drop = (why: string) => {
    for (const w of waiting.values()) w.fail(new Error(why));
    waiting.clear();
  };
  worker.onmessage = (e: MessageEvent<ClashAnswer>) => {
    const w = waiting.get(e.data.n);
    if (!w) return;
    waiting.delete(e.data.n);
    if ('failed' in e.data) w.fail(new Error(e.data.failed));
    else w.done(e.data.answer);
  };
  // A worker that will not start, or dies, answers nothing: everything waiting on it is told.
  worker.onerror = (e) => drop(e.message || 'the collision worker failed');

  // COPIED ACROSS, not given away: the page goes on using its own.
  void ask<null>({
    ask: 'parts',
    parts: parts.map(({ id, mirror, meshes }) => ({
      id, mirror, meshes: meshes.map((g) => ({ position: pointsOf(g), index: cornersOf(g) })),
    })),
  }).catch(() => { /* said again by the question that follows it */ });

  return {
    profile: (asm, step, moments) => ask<ClashProfile>({ ask: 'profile', asm, step, moments }),
    marks: (asm, step, t) => ask<ClashMarks | null>({ ask: 'marks', asm, step, t }),
    hidden: (asm, step, eyes, cams) => ask<number[]>({ ask: 'hidden', asm, step, eyes, cams }),
    overlaps: (asm, pairs) => ask<StandingOverlap[]>({ ask: 'overlaps', asm, pairs }),
    end: () => { ended = true; drop(SCENE_CLOSED); worker.terminate(); },
  };
}
